<template>
  <div class="full-size">
    <CountDownTime v-if="isShowCountDownTime" />
    <div class="full-size" v-show="loaded">
      <div
        class="full-size horizontal layout"
        v-show="!slideActive.showQuestionImage || !slideActive.image"
      >
        <div class="flex line-canvas horizontal layout end">
          <canvas id="line-canvas" />
          <div
            class="hover-label"
            v-show="labelShow"
          >
            {{ labelShow }}
          </div>
          <div
            class="average-slider"
            :style="{'width': hasOneStatement ? '1081px' : '911px'}"
            v-if="!slideActive.hideResult"
          >
            <div
              class="handle-container"
              v-for="(item, key) in config.data.datasets"
              @mouseover="handleMouseOver(item, key)"
              @mouseleave="handleMouseLeave()"
              :key="key"
              :style="{
                'zIndex': 10 - key,
                'left': item.averagePercent + '%'
              }"
              :class="{'high-zindex': labelShow === item.label}"
              v-show="item.averagePoint !== null"
            >
              <span
                class="handle"
                :style="{
                  'backgroundColor': item.borderColor,
                }"
              >{{ item.averagePoint }}</span>
            </div>

            <div
              class="handle-container handle-all"
              :style="{
                'left': config.data.averagePercent + '%'
              }"
              v-if="slideActive.scale.showAverage"
            >
              <span class="line" />
              <span
                class="handle"
                :class="{'black': slideActive.textColour === '#313131'}"
              >{{ config.data.averagePoint || '-' }}</span>
            </div>

            <div
              class="handle-container handle-low-value"
              v-if="isShowScale"
            >
              <span
                class="handle"
              >{{ slideActive.scale.lowValue }}</span>
            </div>
            <div
              class="handle-container handle-high-value"
              v-if="isShowScale"
            >
              <span
                class="handle"
              >{{ slideActive.scale.highValue }}</span>
            </div>
          </div>

          <div class="label-disagree">
            {{ slideActive.scale.lowLabel }}
          </div>
          <div class="label-agree" :style="{ 'right': hasOneStatement ? '-150px' : '-165px' }">
            {{ slideActive.scale.highLabel }}
          </div>
        </div>
        <div
          class="legend horizontal layout center"
          v-if="!hasOneStatement"
        >
          <div class="max-height">
            <div
              class="legend-label"
              v-for="(item, key) in config.data.datasets"
              :key="key"
              v-show="item.id"
              :class="{'d-none': key > 7}"
            >
              <span
                class="legend-label__colour-dot"
                :style="{'backgroundColor': item.oldBorderColor}"
              />
              <div
                @mouseover="handleMouseOver(item, key)"
                @mouseleave="handleMouseLeave()"
                class="label-text"
              >
                {{ item.labelTruncate }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CanvasQuestionImage
        v-show="slideActive.showQuestionImage && slideActive.image"
        @imageLoaded="isLoading = false"
        :isLoading="isLoading"
      />
    </div>
    <div class="full-size horizontal layout center center-justified" v-show="!loaded">
      <div class="text-center">
        <div class="quiz--result">
          {{ $t("loadingResult") }}...
        </div>
        <Loading style="margin: 0 auto" />
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import { mapState, mapActions, mapGetters } from 'vuex'
import Util from '@/util'
import Loading from '@/components/Loading'
import slideApi from '@/api/slide'
import EventBus from '@/eventBus'
import CanvasQuestionImage from '@/components-v2/presentation-editor/middle/CanvasQuestionImage.vue'
import CountDownTime from '@/components/presentations/contents/CountDownTime.vue'

function graphHover(e, array) {
  if (array.length > 0) {
    e.target.style.cursor = 'pointer'
  } else {
    e.target.style.cursor = ''
  }
}

const chartConfig = {
  type: 'line',
  data: {
    labels: [],
    datasets: [
    ],
  },
  options: {
    onHover: graphHover,
    elements: {
      elements: {
        point: {
          radius: 0,
        },
      },
    },
    layout: {
      padding: {
        top: 140,
        bottom: 106,
        left: 100,
        right: 70,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    title: {
      display: false,
    },
    legend: {
      display: false,
    },
    scales: {
      xAxes: [
        {
          display: false,
        },
      ],
      yAxes: [
        {
          display: false,
        },
      ],
    },
  },
}

export default {
  components: {
    Loading,
    CanvasQuestionImage,
    CountDownTime,
  },
  data() {
    return {
      slideId: null,
      showDefaultData: false,
      lineChart: null,
      isLoading: false,
      labelShow: '',
      loaded: false,
      hasOneStatement: false,
      config: { ...chartConfig },
      defaultScale: {
        highLabel: this.$t('Strongly agree'),
        lowLabel: this.$t('Strongly disagree'),
        highValue: 5,
        lowValue: 1,
        0: '',
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
        6: '',
        7: '',
        8: '',
        9: '',
        showAverage: false,
        mustRate: true,
        showMidValues: false,
      },
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
    }),
    ...mapGetters(['isPresenting']),
    isShowCountDownTime() {
      return this.isPresenting && !this.slideActive.hideResult
    },
    isShowScale() {
      return this.slideActive.scale && this.config.data.averagePoint !== null
    },
  },
  watch: {
    '$store.state.slideActive.hideResult': function watchHideResult() {
      if (!this.lineChart) return
      this._checkHideResult()
      this.updateLineChart()
    },
  },
  created() {
    if (this.$store.state.sharePresentation) {
      this.updateSlideActiveAction({
        showQuestionImage: false,
      })
    }
    this.slideId = this.slideActive.id
    let hasOption = false
    this.slideActive.SlideOptions.forEach((item) => {
      if (item.id) hasOption = true
    })
    if (!hasOption) {
      this.showDefaultData = true
    }
    if (!this.slideActive.scale) {
      this.updateSlideActiveAction({
        scale: this.defaultScale,
      })
    }
  },

  beforeDestroy() {
    this.slideId = null
    if (this.lineChart) this.lineChart.destroy()
    this.lineChart = null
  },
  mounted() {
    this._initData()
    EventBus.$on('scaleQuestionAnswer', () => {
      this._updateFromSocket()
      if (!this.slideActive.hasScaleQuestionAnswer) {
        this.updateSlideActiveAction({
          hasScaleQuestionAnswer: true,
        })
      }
    })

    EventBus.$on('changeSlideOption', () => {
      if (this.showDefaultData) {
        this.showDefaultData = false
        if (this.lineChart) this.lineChart.destroy()
        this.lineChart = null
        this._initData()
      } else {
        if (!this.lineChart) return
        this._presenterEditorDetect()
      }
    })

    EventBus.$on('reloadScaleChart', () => {
      if (this.lineChart) this.lineChart.destroy()
      this.lineChart = null
      this._initData()
    })

    this.$store.state.tempAudienceCount = this.$store.state.slideActive.multipleCountAnswer
  },
  destroyed() {
    EventBus.$off('scaleQuestionAnswer')
    EventBus.$off('changeSlideOption')
    EventBus.$off('reloadScaleChart')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    updateLineChart() {
      if (!this.lineChart) return
      this.lineChart.update()
    },
    _hasLargeValue(value) {
      if (!value) return false
      if (value.toString().length > 5) return true
      return false
    },
    _randomeFromTo(max, min) {
      return Math.floor(Math.random() * (max - min)) + min
    },
    _getDefaultChart() {
      this.config.data.labels = [1, 2, 3, 4, 5]
      const datasets = [
        {
          id: 1,
          order: 1,
          oldOrder: 1,
          data: [0.8, 5, 1.2, 0, 0],
          backgroundColor: Util.changeRgbaColor(Util.getAhaColor(0), 0.7),
          borderColor: Util.getAhaColor(0),
          oldBackgroundColor: Util.changeRgbaColor(Util.getAhaColor(0), 0.7),
          oldBorderColor: Util.getAhaColor(0),
          averagePoint: 2,
          averagePercent: 25,
          label: 'Statement 1',
          labelTruncate: 'Statement 1',
        },
        {
          id: 2,
          order: 2,
          oldOrder: 2,
          data: [0, 1, 5, 1, 0],
          backgroundColor: Util.changeRgbaColor(Util.getAhaColor(1), 0.7),
          borderColor: Util.getAhaColor(1),
          oldBackgroundColor: Util.changeRgbaColor(Util.getAhaColor(1), 0.7),
          oldBorderColor: Util.getAhaColor(1),
          averagePoint: 3,
          averagePercent: 50,
          label: 'Statement 2',
          labelTruncate: 'Statement 2',
        },
        {
          id: 3,
          order: 3,
          oldOrder: 3,
          data: [0, 0, 1.2, 5, 0.8],
          backgroundColor: Util.changeRgbaColor(Util.getAhaColor(2), 0.7),
          borderColor: Util.getAhaColor(2),
          oldBackgroundColor: Util.changeRgbaColor(Util.getAhaColor(2), 0.7),
          oldBorderColor: Util.getAhaColor(2),
          averagePoint: 4,
          averagePercent: 75,
          label: 'Statement 3',
          labelTruncate: 'Statement 3',
        },
      ]
      this.config.data.datasets = datasets
      this.config.data.averagePoint = 3
      this.config.data.averagePercent = 50
    },
    async _initData() {
      this.updateSlideActiveAction({
        scaleQuestionLoaded: false,
      })
      this.loaded = false
      this.config = { ...chartConfig }
      this.config.data.datasets = []
      this.config.data.labels = []

      const midValue = this.slideActive.scale.highValue
        - this.slideActive.scale.lowValue

      try {
        const res = await slideApi.slideScaleQuestionById(this.slideActive.id)
        this.loaded = true
        if (this.slideId !== this.slideActive.id) return
        this.config.data.labels = res.body.labels
        this.config.data.datasets = res.body.datasets || []

        if (res.body.datasets && res.body.datasets.length) {
          res.body.datasets.forEach((item) => {
            const averagePercent = item.averagePercent * 100
            item.averagePercent = averagePercent
          })
        }
        this.config.data.averagePoint = res.body.averagePoint
        if (res.body.averagePercent === null) {
          res.body.averagePercent = 0.5
        }
        this.config.data.averagePercent = res.body.averagePercent * 100
        if (this.showDefaultData) {
          this._getDefaultChart()
        }
        this.config.data.datasets.forEach((item) => {
          if (midValue && midValue > 10) {
            item.pointRadius = 0
          }
          if (item.averagePoint !== null && !this.showDefaultData) {
            this.updateSlideActiveAction({
              hasScaleQuestionAnswer: true,
            })
          }
        })
        const createdChart = this._checkForOneStatement()
        if (!createdChart) {
          this._createLineChart()
        }
        this.updateSlideActiveAction({
          scaleQuestionLoaded: true,
        })
      } catch (error) {
        this.setSyncLoadingAction(3)
        throw error
      }
    },
    async _updateFromSocket() {
      const res = await slideApi.slideScaleQuestionById(this.slideActive.id)
      // this.config.data.labels = res.body.labels
      this.config.data.datasets.forEach((item) => {
        const index = res.body.datasets.findIndex((data) => {
          return data.id === item.id
        })
        if (index !== -1) {
          item.data = res.body.datasets[index].data
          item.oldData = [...res.body.datasets[index].data]
          item.averagePoint = res.body.datasets[index].averagePoint
          item.averagePercent = res.body.datasets[index].averagePercent * 100
        }
      })
      this.config.data.labels = res.body.labels
      this.config.data.averagePoint = res.body.averagePoint
      this.config.data.averagePercent = res.body.averagePercent * 100
      if (res.body.averagePercent === null) {
        this.config.data.averagePercent = 50
      }
      this._checkHideResult()
      this.updateLineChart()
    },
    _presenterEditorDetect() {
      const datasets = []
      let highValue = 5
      let lowValue = 1
      if (this.slideActive.scale) {
        highValue = this.slideActive.scale.highValue
        lowValue = this.slideActive.scale.lowValue
      }
      const middleValue = highValue - lowValue + 1
      this.slideActive.SlideOptions.forEach((item) => {
        const index = this.config.data.datasets.findIndex((dataset) => {
          return dataset.id === item.id
        })
        if (index !== -1) {
          const objDataSet = this.config.data.datasets[index]
          objDataSet.label = item.title
          objDataSet.order = datasets.length + 1
          datasets.push(objDataSet)
        } else {
          if (!item.order) {
            item.order = datasets.length + 1
          }
          const obj = {
            label: item.title,
            id: item.id,
            order: item.order,
            averagePoint: null,
            averagePercent: null,
            data: new Array(middleValue).fill(0),
          }
          if (middleValue > 10) {
            obj.pointRadius = 0
          }
          datasets.push(obj)
        }
      })
      this.config.data.datasets = datasets
      this._setOldDataLineChart()
      const createdChart = this._checkForOneStatement()
      if (!createdChart) {
        this.config.data.datasets.push()
        this.updateLineChart()
      }
    },
    _checkHideResult() {
      if (this.slideActive.hideResult) {
        this.config.data.datasets.forEach((item) => {
          for (let i = 0; i < item.data.length; i++) {
            item.data[i] = 0
          }
        })
      } else {
        this.config.data.datasets.forEach((item) => {
          for (let i = 0; i < item.data.length; i++) {
            item.data[i] = item.oldData[i]
          }
        })
      }
    },

    _setOldDataLineChart() {
      let limitTextTruncate = 60
      if (this.config.data.datasets.length === 4) {
        limitTextTruncate = 40
      } else if (this.config.data.datasets.length === 5) {
        limitTextTruncate = 30
      } else if (this.config.data.datasets.length === 6) {
        limitTextTruncate = 30
      } else if (this.config.data.datasets.length === 7) {
        limitTextTruncate = 20
      } else if (this.config.data.datasets.length === 8) {
        limitTextTruncate = 15
      } else if (this.config.data.datasets.length === 9) {
        limitTextTruncate = 15
      }
      this.config.data.datasets.forEach((item, key) => {
        item.backgroundColor = Util.changeRgbaColor(Util.getAhaColor(key), 0.7)
        item.borderColor = Util.getAhaColor(key)
        item.oldBackgroundColor = Util.changeRgbaColor(Util.getAhaColor(key), 0.7)
        item.oldBorderColor = Util.getAhaColor(key)
        item.oldOrder = item.order
        item.borderWidth = 4
        if (!item.oldData || !this.slideActive.hideResult) {
          item.oldData = [...item.data]
        }
        item.labelTruncate = Util.truncate(item.label, limitTextTruncate)
      })
      this.config.data.datasets.push()
    },
    _checkForOneStatement() {
      this.labelShow = ''
      const oldValue = this.hasOneStatement
      if (this.config.data.datasets.length <= 1) {
        this.config.options.layout.padding.right = 100
        this.hasOneStatement = true
        if (this.config.data.datasets.length && this.config.data.datasets[0].label) {
          this.labelShow = this.config.data.datasets[0].label
        }
      } else {
        this.config.options.layout.padding.right = 70
        this.hasOneStatement = false
        // Trigger recreat chart because updating is not enough
        if (oldValue) {
          this._initData()
          return true
        }
      }
      return false
    },
    _createLineChart() {
      this._setOldDataLineChart()
      this._checkHideResult()
      this.config.data.datasets.push()
      if (this.lineChart) this.lineChart.destroy()
      this.lineChart = null
      if (!document.getElementById('line-canvas')) return
      const ctx = document.getElementById('line-canvas').getContext('2d')
      this.lineChart = new Chart(ctx, this.config)
    },
    handleMouseOver(item, key) {
      if (this.labelShow) return
      this.labelShow = item.label
      if (this.slideActive.hideResult) return
      this.config.data.datasets.forEach((data, index) => {
        this.config.data.datasets[index].oldBackgroundColor = this.config.data.datasets[index].backgroundColor
        this.config.data.datasets[index].oldBorderColor = this.config.data.datasets[index].borderColor
        this.config.data.datasets[index].oldOrder = this.config.data.datasets[index].order
        if (index !== key) {
          this.config.data.datasets[index].backgroundColor = 'rgba(240, 240, 240, 0)'
          this.config.data.datasets[index].borderColor = Util.changeRgbaColor(Util.getAhaColor(index), 0.2)
        } else {
          this.config.data.datasets[index].order = 1
          this.config.data.datasets[0].order = this.config.data.datasets[index].oldOrder
        }
      })

      this.config.data.datasets.push()
      this.updateLineChart()
    },
    handleMouseLeave() {
      if (this.hasOneStatement) return
      this.labelShow = ''
      if (this.slideActive.hideResult) return
      this.config.data.datasets.forEach((data, index) => {
        this.config.data.datasets[index].backgroundColor =
            this.config.data.datasets[index].oldBackgroundColor
        this.config.data.datasets[index].borderColor =
            this.config.data.datasets[index].oldBorderColor
        this.config.data.datasets[index].order = this.config.data.datasets[index].oldOrder
      })
      this.config.data.datasets.push()
      this.updateLineChart()
    },
  },
}
</script>

<style lang="scss" scoped>

#line-canvas {
  position: relative;
  z-index: 10;
}
.line-canvas {
  position: relative;

  #line-canvas {
    max-width: 100%;
  }
}

.label-disagree {
    position: absolute;
    top: 50%;
    left: -140px;
    transform: rotate(-90deg);
    font-weight: bold;
    font-size: 22px;
    width: 400px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-top: -50px;
}

.label-agree {
    position: absolute;
    top: 50%;
    right: -165px;
    transform: rotate(-90deg);
    font-weight: bold;
    font-size: 22px;
    width: 400px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-top: -50px;
}

.legend {
  width: 200px;
  padding-right: 30px;
  overflow-y: scroll;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  max-height: 100%;
  line-height: 1.3;

  .max-height {
    max-height: 100%;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  .max-height:hover {
    .legend-label {
      .label-text {
        opacity: 0.5;
      }
    }
  }

  .legend-label {
    position: relative;
    margin-bottom: 0.25rem;
    &::v-deep {
      .mce-editor {
        margin-left: 1.5rem;
        max-width: 50px;
      }
    }

    .legend-label__colour-dot {
      display: block;
      width: 20px;
      height: 20px;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 9;
      border-radius: 50%;
      border: 1px solid #fff;
    }

    .label-text {
      padding-left: 30px;
      padding-bottom: 8px;
      position: relative;
      z-index: 10;
      max-width: 100%;
      word-break: break-word;
      font-size: 16px;
      opacity: 1;
      &:hover {
        opacity: 1 !important;
      }
    }
  }
}

.hover-label {
  position: absolute;
  top: 7%;
  left: 100px;
  font-size: 20px;
  text-align: center;
  font-size: 28px;
  line-height: 1.3;
  right: 70px;
}

.quiz--result {
  font-size: 30px;
}

.average-slider {
  position: absolute;
  left: 100px;
  bottom: 132px;
  z-index: 100;
  transform: translateZ(+2000000000000px);

  .handle-container {
    position: absolute;
    transform: translateX(-50%);
    transition:left 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    &.high-zindex {
      z-index: 1000 !important;
    }

    .line {
      position: absolute;
      height: 300px;
      bottom: 50px;
      width: 4px;
      background-color: #cccccc61;
      transform: translateX(-50%);
      left: calc(50% - 1px);
    }

    .handle {
      padding: 14.5px 10px 14px;
      border: 1px solid #fff;
      color: #fff;
      font-size: 20px;
      cursor: pointer;
      min-width: 51px;
      font-weight: bold;
      text-align: center;
      line-height: 1;
      display: block;
      border-radius: 999px;
      margin: 1px;
      animation: all 1s cubic-bezier(0.25, 1, 0.5, 1);

      &.black {
        color: #313131;
        border-color: #313131;
      }
    }

    &.handle-all {
      bottom: -110px;
    }

    &.handle-low-value {
      z-index: 0;
      top: 18px;
      left: 0%;

      .handle {
        color: inherit;
        border: 0;
        border-radius: 0;
        font-weight: normal;
        font-size: 18px;
        opacity: 0.7;
      }
    }

    &.handle-high-value {
      z-index: 0;
      top: 18px;
      left: 100%;

      .handle {
        color: inherit;
        border: 0;
        border-radius: 0;
        font-weight: normal;
        font-size: 18px;
        opacity: 0.7;
      }
    }
  }
}

.average-line-container {
  position: absolute;
  left: 0;
  bottom: 0;
}
</style>
